import React, { useMemo, useCallback } from 'react';
import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  Grid,
  ListSubheader,
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import type { ExpenseFilter } from '../../../types/expense';
import type { Supplier } from '../../../types/supplier';
import type { CategoryOption } from '../../../constants/khaataCategories';
import {
  KHAATA_TYPES,
  getCategoriesForType,
  getCategoriesGroupedForEntry,
  formatCategoryLabel,
  isCategoryEnabled,
} from '../../../constants/khaataCategories';
import {
  renderSupplierMenuItem,
  getSupplierSelectLabel,
  khaataSelectMenuProps,
} from './SupplierSelectOption';
import './Style.scss';

interface ExpenseFiltersProps {
  filter: ExpenseFilter;
  onFilterChange: (filter: ExpenseFilter) => void;
  suppliers?: Supplier[];
  categoryOptions?: CategoryOption[];
}

const filterSelectSx = {
  '& .MuiSelect-select': {
    py: '6px',
    fontSize: '0.8125rem',
  },
};

const filterControlSx = {
  '& .MuiInputLabel-root': {
    fontSize: '0.8125rem',
    '&.MuiInputLabel-shrink': {
      backgroundColor: '#fff',
      px: 0.5,
    },
  },
};

const ExpenseFiltersComponent: React.FC<ExpenseFiltersProps> = ({
  filter,
  onFilterChange,
  suppliers = [],
  categoryOptions: allCategoryOptions,
}) => {
  const currentYear = new Date().getFullYear();
  const years = useMemo(
    () => Array.from({ length: 5 }, (_, i) => currentYear - i),
    [currentYear]
  );
  const supplierTypes = ['all', 'purchase', 'asset'];
  const showSupplierFilter =
    supplierTypes.includes(filter.type || 'all') && suppliers.length > 0;

  const isSpecificType = !!filter.type && filter.type !== 'all';

  const categoryChoices = useMemo(
    () =>
      isSpecificType
        ? getCategoriesForType(
            filter.type as Exclude<ExpenseFilter['type'], 'all' | undefined>,
            allCategoryOptions
          ).filter((c) => isCategoryEnabled(c) || c.value === filter.category)
        : [],
    [isSpecificType, filter.type, filter.category, allCategoryOptions]
  );

  const categoryGroups = useMemo(
    () => getCategoriesGroupedForEntry(allCategoryOptions, filter.category),
    [allCategoryOptions, filter.category]
  );

  const handlePeriodChange = useCallback(
    (event: SelectChangeEvent) => {
      const period = event.target.value as ExpenseFilter['period'];
      onFilterChange({
        ...filter,
        period,
        ...(period === 'yearly' ? {} : { year: years[0] }),
      });
    },
    [filter, onFilterChange, years]
  );

  const handleYearChange = useCallback(
    (event: SelectChangeEvent) => {
      onFilterChange({
        ...filter,
        year: Number(event.target.value),
      });
    },
    [filter, onFilterChange]
  );

  const handleFromDateChange = useCallback(
    (value: Dayjs | null) => {
      onFilterChange({
        ...filter,
        fromDate: value ? value.format('YYYY-MM-DD') : undefined,
      });
    },
    [filter, onFilterChange]
  );

  const handleToDateChange = useCallback(
    (value: Dayjs | null) => {
      onFilterChange({
        ...filter,
        toDate: value ? value.format('YYYY-MM-DD') : undefined,
      });
    },
    [filter, onFilterChange]
  );

  const handleTypeChange = useCallback(
    (event: SelectChangeEvent) => {
      const type = event.target.value as ExpenseFilter['type'];
      onFilterChange({
        ...filter,
        type,
        category: undefined,
        supplierId: type === 'expense' ? undefined : filter.supplierId,
      });
    },
    [filter, onFilterChange]
  );

  const handleCategoryChange = useCallback(
    (event: SelectChangeEvent) => {
      const value = event.target.value;
      onFilterChange({
        ...filter,
        category: value === 'all' ? undefined : value,
      });
    },
    [filter, onFilterChange]
  );

  const handleSupplierChange = useCallback(
    (event: SelectChangeEvent) => {
      const value = event.target.value;
      onFilterChange({
        ...filter,
        supplierId: value === 'all' ? undefined : value,
      });
    },
    [filter, onFilterChange]
  );

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box className="khaata-filters" sx={{ mb: 1, mt: { xs: 0.5, sm: 1.5 } }}>
        <Grid container spacing={{ xs: 1.5, sm: 1.5, lg: 1 }} alignItems="center">
          <Grid item xs={6} sm={6} lg={3}>
            <FormControl fullWidth size="small" className="updatedFilterInput khaata-filters__control" sx={filterControlSx}>
              <InputLabel>View By</InputLabel>
              <Select
                {...khaataSelectMenuProps}
                sx={filterSelectSx}
                value={filter.period}
                label="View By"
                onChange={handlePeriodChange}
              >
                <MenuItem value="daily">Today</MenuItem>
                <MenuItem value="weekly">This Week</MenuItem>
                <MenuItem value="monthly">This Month</MenuItem>
                <MenuItem value="yearly">Yearly</MenuItem>
                <MenuItem value="custom">Custom</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={6} sm={6} lg={3}>
            <FormControl fullWidth size="small" className="updatedFilterInput khaata-filters__control" sx={filterControlSx}>
              <InputLabel>Type</InputLabel>
              <Select
                {...khaataSelectMenuProps}
                sx={filterSelectSx}
                value={filter.type || 'all'}
                label="Type"
                onChange={handleTypeChange}
              >
                <MenuItem value="all">All Types</MenuItem>
                {KHAATA_TYPES.map((t) => (
                  <MenuItem key={t.value} value={t.value}>
                    {t.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={6} sm={6} lg={3}>
            <FormControl fullWidth size="small" className="updatedFilterInput khaata-filters__control" sx={filterControlSx}>
              <InputLabel>Category</InputLabel>
              <Select
                {...khaataSelectMenuProps}
                sx={filterSelectSx}
                value={filter.category || 'all'}
                label="Category"
                onChange={handleCategoryChange}
                renderValue={(selected) =>
                  !selected || selected === 'all'
                    ? 'All Categories'
                    : formatCategoryLabel(selected as string, allCategoryOptions)
                }
              >
                <MenuItem value="all">All Categories</MenuItem>
                {isSpecificType
                  ? categoryChoices.map((cat) => (
                      <MenuItem
                        key={cat.value}
                        value={cat.value}
                        sx={{ fontSize: '0.8125rem' }}
                      >
                        {cat.label}
                      </MenuItem>
                    ))
                  : categoryGroups.map((group) => [
                      <ListSubheader
                        key={`header-${group.type}`}
                        sx={{
                          fontWeight: 700,
                          fontSize: '0.75rem',
                          lineHeight: '30px',
                          textTransform: 'uppercase',
                          letterSpacing: '0.04em',
                          bgcolor: 'grey.100',
                          color: 'text.secondary',
                        }}
                      >
                        {group.label}
                      </ListSubheader>,
                      ...group.categories.map((cat) => (
                        <MenuItem
                          key={cat.value}
                          value={cat.value}
                          sx={{ pl: 3, fontSize: '0.8125rem' }}
                        >
                          {cat.label}
                        </MenuItem>
                      )),
                    ])}
              </Select>
            </FormControl>
          </Grid>

          {showSupplierFilter ? (
            <Grid item xs={6} sm={6} lg={3}>
              <FormControl
                fullWidth
                size="small"
                className="updatedFilterInput khaata-filters__control khaata-filters__control--supplier"
                sx={filterControlSx}
              >
                <InputLabel id="khaata-supplier-filter-label">Supplier</InputLabel>
                <Select
                  {...khaataSelectMenuProps}
                  labelId="khaata-supplier-filter-label"
                  sx={filterSelectSx}
                  value={filter.supplierId || 'all'}
                  label="Supplier"
                  onChange={handleSupplierChange}
                  renderValue={(selected) =>
                    getSupplierSelectLabel(suppliers, selected as string)
                  }
                >
                  <MenuItem value="all">All Suppliers</MenuItem>
                  {suppliers.map((supplier) => renderSupplierMenuItem(supplier))}
                </Select>
              </FormControl>
            </Grid>
          ) : null}

          {filter.period === 'yearly' && (
            <Grid item xs={12} sm={showSupplierFilter ? 6 : 12} lg={3}>
              <FormControl fullWidth size="small" className="updatedFilterInput khaata-filters__control" sx={filterControlSx}>
                <InputLabel>Year</InputLabel>
                <Select
                  {...khaataSelectMenuProps}
                  sx={filterSelectSx}
                  value={filter.year.toString()}
                  label="Year"
                  onChange={handleYearChange}
                >
                  {years.map((year) => (
                    <MenuItem key={year} value={year}>
                      {year}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          )}

          {filter.period === 'custom' && (
            <Grid item xs={12} lg={4} className="khaata-filters__custom-dates">
              <Grid container spacing={1.5}>
                <Grid item xs={12} sm={6} className="khaata-filters__date-field">
                  <DatePicker
                    label="From"
                    value={filter.fromDate ? dayjs(filter.fromDate) : null}
                    onChange={handleFromDateChange}
                    slotProps={{
                      textField: {
                        size: 'small',
                        fullWidth: true,
                        className: 'updatedFilterInput khaata-filters__control khaata-filters__date-input',
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} className="khaata-filters__date-field">
                  <DatePicker
                    label="To"
                    value={filter.toDate ? dayjs(filter.toDate) : null}
                    onChange={handleToDateChange}
                    slotProps={{
                      textField: {
                        size: 'small',
                        fullWidth: true,
                        className: 'updatedFilterInput khaata-filters__control khaata-filters__date-input',
                      },
                    }}
                  />
                </Grid>
              </Grid>
            </Grid>
          )}
        </Grid>
      </Box>
    </LocalizationProvider>
  );
};

export const ExpenseFilters = React.memo(ExpenseFiltersComponent);
